import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, FileText, RefreshCw, TrendingUp, Users, Layers3, ListChecks, GitCompareArrows, ClipboardList, Search } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { reportsAPI, averagereportsAPI, reportPdfAPI } from '../../services/api';
import { downloadBlobResponse } from '../../utils/downloadFile';
import './reportsOdoo.css';

const REPORTS = [
  { id: 'participation', no: 4, name: 'Response & Participation', icon: Users },
  { id: 'distribution', no: 5, name: 'Rating Distribution', icon: BarChart3 },
  { id: 'category', no: 6, name: 'Category Performance', icon: Layers3 },
  { id: 'question', no: 7, name: 'Question Performance', icon: ListChecks },
  { id: 'organization', no: 8, name: 'Organizational Comparison', icon: GitCompareArrows },
  { id: 'trend', no: 9, name: 'Trend / Period Comparison', icon: TrendingUp },
  { id: 'summary', no: 10, name: 'Management Summary', icon: ClipboardList },
];

const card = 'bg-white border border-[#e7e9ef] rounded-xl shadow-[0_1px_3px_rgba(15,23,42,.06)]';
const th = 'px-4 py-3 bg-[#f7f8fa] text-left text-[11px] font-semibold uppercase tracking-wide text-[#667085] border-b border-[#e7e9ef]';
const td = 'px-4 py-3 border-b border-[#eef0f4] text-sm text-[#344054]';

const pct = (n) => `${Number(n || 0).toFixed(1)}%`;
const avg = (n) => Number(n || 0).toFixed(2);
const prevRange = (from, to) => {
  if (!from || !to) return null;
  const a = new Date(`${from}T00:00:00`), b = new Date(`${to}T00:00:00`);
  const days = Math.max(1, Math.round((b-a)/86400000)+1);
  const pb = new Date(a); pb.setDate(pb.getDate()-1);
  const pa = new Date(pb); pa.setDate(pa.getDate()-days+1);
  return { from: pa.toISOString().slice(0,10), to: pb.toISOString().slice(0,10) };
};

const REPORT_IDS = REPORTS.map(r => r.id);

export default function ReportsCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [surveys, setSurveys] = useState([]);
  const [surveyId, setSurveyId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [report, setReport] = useState(null);
  const [org, setOrg] = useState(null);
  // The sidebar now links straight to each report tab via "?tab=<id>" (it
  // used to only link to this page with no way to land on a specific tab,
  // which is why people kept missing the 7 report types entirely). Read
  // the initial tab from the URL instead of always defaulting to the
  // first one.
  const initialTab = searchParams.get('tab');
  const [active, setActiveState] = useState(REPORT_IDS.includes(initialTab) ? initialTab : 'participation');

  // Keep the tab in sync both ways: clicking a tab updates the URL (so the
  // sidebar highlight and the browser's back button both work), and
  // navigating to a different "?tab=" link while already on this page
  // (React Router doesn't remount here since the pathname is unchanged)
  // updates the active tab.
  const setActive = useCallback((id) => {
    setActiveState(id);
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('tab', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && REPORT_IDS.includes(tab) && tab !== active) {
      setActiveState(tab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = useCallback(async (silent = false) => {
    try {
      if (silent) setRefreshing(true);
      else setLoading(true);

      setError('');

      let sid = surveyId;
      let surveyList = surveys;

      if (!sid) {
        const surveyResponse = await averagereportsAPI.getSurveys();
        surveyList = surveyResponse.data || [];
        setSurveys(surveyList);

        sid = surveyList?.[0]?.id || '';

        if (sid) setSurveyId(String(sid));
      }

      if (!sid) {
        setLoading(false);
        setRefreshing(false);
        return;
      }

      const request = {
        surveyId: Number(sid),
        employeeIds: [],
        fromDate: fromDate
          ? new Date(`${fromDate}T00:00:00`)
          : null,
        toDate: toDate
          ? new Date(`${toDate}T23:59:59`)
          : null
      };

      const [a, companies] = await Promise.all([
        averagereportsAPI.generateReport(request),
        reportsAPI.getCompanies()
      ]);

      setReport(a.data);

      const selected = surveyList.find(
        x => String(x.id) === String(sid)
      );

      const companyId =
        selected?.companyId ||
        companies.data?.[0]?.id;

      if (companyId) {
        const o = await reportsAPI.generateReport({
          companyId,
          surveyIds: [Number(sid)],
          branchIds: [],
          departmentIds: [],
          divisionIds: [],
          fromDate: request.fromDate,
          toDate: request.toDate,
          IncludeBranchReport: true,
          IncludeDepartmentReport: true,
          IncludeDivisionReport: true
        });

        setOrg(o.data);
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data ||
        e.message ||
        'Failed to load reports'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [surveyId, fromDate, toDate, surveys]);

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!surveyId) return;
    const timer = setInterval(() => load(true), 30000);
    return () => clearInterval(timer);
  }, [surveyId, fromDate, toDate, load]);

  const categories = report?.categories || [];
  const questions = useMemo(() => categories.flatMap(c => (c.questions || []).map(q => ({...q, categoryName:c.categoryName}))), [categories]);
  const totalQuestions = questions.length;
  const selectedSurvey = surveys.find(s => String(s.id) === String(surveyId));
  const currentAverage = Number(report?.overallScore || 0);
  const totalResponses = Number(report?.totalResponses || 0);
  const totalEmployees = Number(report?.totalEmployees || 0);
  const responseRate = Number(report?.responseRate || 0);
  const ratingCounts = useMemo(() => {
    const r = {1:0,2:0,3:0,4:0,5:0};
    questions.forEach(q => { const d=q.ratingDistribution || {}; [1,2,3,4,5].forEach(x => r[x]+=Number(d[`rating${x}`] ?? d[`rating${x}Count`] ?? 0)); });
    return r;
  }, [questions]);
  const totalRatings = Object.values(ratingCounts).reduce((a,b)=>a+b,0);
  const measures = {
    gt4: totalRatings ? ratingCounts[5]/totalRatings*100 : 0,
    eq4: totalRatings ? ratingCounts[4]/totalRatings*100 : 0,
    lt4: totalRatings ? (ratingCounts[1]+ratingCounts[2]+ratingCounts[3])/totalRatings*100 : 0,
    lt3: totalRatings ? (ratingCounts[1]+ratingCounts[2])/totalRatings*100 : 0,
    fourPlus: totalRatings ? (ratingCounts[4]+ratingCounts[5])/totalRatings*100 : 0,
  };
  const sortedCats = [...categories].sort((a,b)=>Number(b.averageScore)-Number(a.averageScore));
  const range = prevRange(fromDate,toDate);
  const [previous,setPrevious] = useState(null);
  useEffect(() => {
    let cancelled=false;
    if (!range || !surveyId) { setPrevious(null); return; }
    averagereportsAPI.generateReport({surveyId:Number(surveyId),employeeIds:[],fromDate:new Date(`${range.from}T00:00:00`),toDate:new Date(`${range.to}T23:59:59`)}).then(r=>{if(!cancelled)setPrevious(r.data)}).catch(()=>{if(!cancelled)setPrevious(null)});
    return ()=>{cancelled=true};
  }, [surveyId, range?.from, range?.to]);

  const exportPdf = async () => {
    if (!surveyId) {
      setError('Please select a survey first');
      return;
    }
    try {
      setExportingPdf(true);
      setError('');
      const response = await reportPdfAPI.exportReport({
        surveyId: Number(surveyId),
        reportType: active,
        fromDate: fromDate ? new Date(`${fromDate}T00:00:00`) : null,
        toDate: toDate ? new Date(`${toDate}T23:59:59`) : null
      });
      const reportName = REPORTS.find(x => x.id === active)?.name || 'survey-report';
      const filename = `${reportName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.pdf`;
      downloadBlobResponse(response, filename);
    } catch (e) {
      console.error('PDF export failed:', e);
      setError(e?.response?.data || e?.message || 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };
  const orgRows = org?.branchReports || [];
  const deptRows = org?.departmentReports || [];
  const divRows = org?.divisionReports || [];

  if (loading && !report) {
    return (
      <div className="gw-odoo-report-loading">
        Loading Reports…
      </div>
    );
  }

  const activeReport = REPORTS.find(x => x.id === active);

  return (
    <div className="gw-odoo-report-page">
      <div className="gw-odoo-report-shell">

        <div className="gw-odoo-control-panel">
          <div className="gw-odoo-title-row">
            <div className="gw-odoo-title-group">
              <div className="gw-odoo-breadcrumb">Reports</div>
              <div className="gw-odoo-page-title">
                {activeReport?.name || 'Reports'}
              </div>
            </div>

            <div className="gw-odoo-actions no-print">
              <button
                type="button"
                onClick={() => load(true)}
                disabled={refreshing}
                className="gw-odoo-btn gw-odoo-btn-secondary"
              >
                <RefreshCw
                  size={15}
                  className={refreshing ? 'animate-spin' : ''}
                />
                Refresh
              </button>

              <button
                type="button"
                onClick={exportPdf}
                disabled={exportingPdf || !surveyId}
                className="gw-odoo-btn gw-odoo-btn-primary"
              >
                <FileText size={15} />
                {exportingPdf ? 'Exporting…' : `Export ${activeReport?.name || 'Report'} PDF`}
              </button>
            </div>
          </div>

          <div className="gw-odoo-search-panel no-print">
            <div className="gw-odoo-field">
              <label>Survey</label>
              <select
                value={surveyId}
                onChange={e => setSurveyId(e.target.value)}
              >
                <option value="">Select survey</option>
                {surveys.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="gw-odoo-field">
              <label>
                From Date
                <span>Optional</span>
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
              />
            </div>

            <div className="gw-odoo-field">
              <label>
                To Date
                <span>Optional</span>
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
              />
            </div>

            <button
              type="button"
              onClick={() => load(true)}
              disabled={!surveyId || refreshing}
              className="gw-odoo-search-btn"
            >
              <Search
                size={15}
                className={refreshing ? 'animate-pulse' : ''}
              />
              {refreshing ? 'Searching…' : 'Search'}
            </button>
          </div>

          <div className="gw-odoo-search-help no-print">
            Leave the date fields empty to search the complete selected survey period.
          </div>

          {error && (
            <div className="gw-odoo-error">
              {typeof error === 'string'
                ? error
                : JSON.stringify(error)}
            </div>
          )}
        </div>

        <div className="gw-odoo-report-tabs no-print">
          {REPORTS.map(r => {
            const Icon = r.icon;

            return (
              <button
                key={r.id}
                type="button"
                onClick={() => setActive(r.id)}
                className={active === r.id ? 'active' : ''}
              >
                <Icon size={15} />
                <span>
                  {r.no}. {r.name}
                </span>
              </button>
            );
          })}
        </div>

        <main className="gw-odoo-report-content">

          <section className="gw-odoo-report-sheet print-card">
            <div className="gw-odoo-report-sheet-head">
              <div>
                <div className="gw-odoo-report-kicker">
                  Great Workplace Club / Reports
                </div>

                <h2>
                  {activeReport?.no}. {activeReport?.name}
                </h2>

                <p>
                  {selectedSurvey?.name || 'Survey'}
                  {' · '}
                  {report?.surveyPeriod || 'Selected reporting period'}
                </p>
              </div>

              <div className="gw-odoo-report-meta">
                <span>Last refreshed</span>
                <strong>
                  {new Date().toLocaleTimeString()}
                </strong>
              </div>
            </div>

            <div className="gw-odoo-stat-grid">
              <Metric
                label="Responses"
                value={totalResponses.toLocaleString()}
                sub={`${responseRate.toFixed(1)}% response rate`}
              />

              <Metric
                label="Employees"
                value={totalEmployees.toLocaleString()}
                sub="Eligible population"
              />

              <Metric
                label="Questions"
                value={totalQuestions.toLocaleString()}
                sub={`${categories.length} categories`}
              />

              <Metric
                label="Average"
                value={`${avg(currentAverage)} / 5`}
                sub="Overall score"
              />

              <Metric
                label="4 or More"
                value={pct(measures.fourPlus)}
                sub="Ratings 4 + 5"
              />
            </div>
          </section>

          {active === 'participation' && (
            <Participation
              totalEmployees={totalEmployees}
              totalResponses={totalResponses}
              responseRate={responseRate}
              orgRows={orgRows}
              deptRows={deptRows}
              divRows={divRows}
            />
          )}

          {active === 'distribution' && (
            <Distribution
              counts={ratingCounts}
              total={totalRatings}
              measures={measures}
              categories={categories}
            />
          )}

          {active === 'category' && (
            <Category categories={sortedCats} />
          )}

          {active === 'question' && (
            <Question categories={categories} />
          )}

          {active === 'organization' && (
            <Organization
              branches={orgRows}
              departments={deptRows}
              divisions={divRows}
            />
          )}

          {active === 'trend' && (
            <Trend
              current={report}
              previous={previous}
              range={range}
            />
          )}

          {active === 'summary' && (
            <Summary
              report={report}
              categories={sortedCats}
              measures={measures}
              responseRate={responseRate}
            />
          )}

        </main>
      </div>
    </div>
  );

}

function Metric({label,value,sub}){return <div className="bg-[#f8f9fb] border border-[#edf0f4] rounded-lg p-3"><div className="text-[10px] uppercase tracking-wide font-bold text-[#667085]">{label}</div><div className="text-xl font-bold text-[#182230] mt-1">{value}</div><div className="text-[10px] text-[#667085] mt-1">{sub}</div></div>}
function Box({title,note,children}){return <section className={`${card} p-5 mt-4 print-card`}><h3 className="font-bold text-[#182230]">{title}</h3><p className="text-xs text-[#667085] mt-1 mb-4">{note}</p>{children}</section>}
function Participation({totalEmployees,totalResponses,responseRate,orgRows,deptRows,divRows}){return <><Box title="Participation Funnel" note="Live counts calculated from the selected survey and filters"><div className="grid md:grid-cols-3 gap-3">{[['Invited',totalEmployees,100],['Completed',totalResponses,responseRate],['Not completed',Math.max(0,totalEmployees-totalResponses),Math.max(0,100-responseRate)]].map(x=><div key={x[0]} className="border border-[#e7e9ef] rounded-lg p-4"><div className="text-xs text-[#667085]">{x[0]}</div><div className="text-2xl font-bold mt-1">{x[1].toLocaleString()}</div><div className="mt-3 h-2 bg-[#edf0f5] rounded-full overflow-hidden"><div className="h-full bg-[#2447d8]" style={{width:`${Math.min(100,x[2])}%`}}/></div><div className="text-xs text-[#667085] mt-1">{x[2].toFixed(1)}%</div></div>)}</div></Box><OrgParticipation title="Branch Participation" rows={orgRows} name="branchName"/><OrgParticipation title="Department Participation" rows={deptRows} name="departmentName"/><OrgParticipation title="Division Participation" rows={divRows} name="divisionName"/></>}
function OrgParticipation({title,rows,name}){return <Box title={title} note="Participation rate returned by the reporting API"><div className="overflow-auto"><table className="w-full"><thead><tr><th className={th}>Name</th><th className={th}>Participation</th><th className={th}>Category averages</th></tr></thead><tbody>{rows.map((r,i)=><tr key={i}><td className={td}><strong>{r[name]}</strong></td><td className={td}><span className="font-bold text-[#2447d8]">{pct(r.participationRate)}</span></td><td className={td}>{Object.entries(r.categoryAverages||{}).map(([k,v])=><span key={k} className="inline-block mr-3 text-xs">{k}: <b>{avg(v)}</b></span>)}</td></tr>)}</tbody></table></div></Box>}
function Distribution({counts,total,measures,categories}){return <><Box title="Overall Rating Distribution" note="Rating 1 through 5 across all available question responses"><div className="space-y-3">{[5,4,3,2,1].map(r=><div key={r} className="grid grid-cols-[90px_1fr_80px] gap-3 items-center"><div className="font-semibold">Rating {r}</div><div className="h-3 bg-[#edf0f5] rounded-full overflow-hidden"><div className="h-full bg-[#2447d8]" style={{width:`${total?counts[r]/total*100:0}%`}}/></div><div className="text-right font-bold">{total?(counts[r]/total*100).toFixed(1):'0.0'}%</div></div>)}</div><div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-5">{[['>4',measures.gt4],['=4',measures.eq4],['<4',measures.lt4],['<3',measures.lt3],['4 or More',measures.fourPlus]].map(([a,b])=><div className="border border-[#e7e9ef] rounded-lg p-3" key={a}><div className="text-xs text-[#667085]">{a}</div><b className="text-lg">{b.toFixed(1)}%</b></div>)}</div></Box><Box title="Category Rating Distribution" note="Category-level average score and available rating distribution"><div className="overflow-auto"><table className="w-full"><thead><tr><th className={th}>Category</th><th className={th}>Questions</th><th className={th}>Average / 5</th></tr></thead><tbody>{categories.map(c=><tr key={c.categoryName}><td className={td}><strong>{c.categoryName}</strong></td><td className={td}>{(c.questions||[]).length}</td><td className={td}><b>{avg(c.averageScore)}</b></td></tr>)}</tbody></table></div></Box></>}
function Category({categories}){const data=categories.map(c=>({name:c.categoryName,score:Number(c.averageScore||0)}));return <><Box title="Category Performance Chart" note="Live category averages from the selected survey"><div style={{width:'100%',height:320}}><ResponsiveContainer><BarChart data={data} margin={{top:10,right:15,left:0,bottom:55}}><CartesianGrid strokeDasharray="3 3" stroke="#e7e9ef"/><XAxis dataKey="name" angle={-30} textAnchor="end" height={75} tick={{fontSize:11}}/><YAxis domain={[0,5]} tick={{fontSize:11}}/><Tooltip formatter={(v)=>[`${Number(v).toFixed(2)} / 5`,'Average']}/><Bar dataKey="score" fill="#2447d8" radius={[6,6,0,0]}/></BarChart></ResponsiveContainer></div></Box><Box title="Category Performance" note="Ranked from highest to lowest category average"><div className="space-y-4">{categories.map((c,i)=><div key={c.categoryName}><div className="flex justify-between mb-1"><span className="font-semibold">{i+1}. {c.categoryName}</span><b>{avg(c.averageScore)} / 5</b></div><div className="h-3 bg-[#edf0f5] rounded-full overflow-hidden"><div className="h-full bg-[#2447d8]" style={{width:`${Math.min(100,Number(c.averageScore)/5*100)}%`}}/></div></div>)}</div></Box></>}
function Question({categories}){return <Box title="Question Performance" note="Click a category to expand all questions; employee identities are never displayed"><div className="space-y-2">{categories.map((c,i)=><details key={c.categoryName} className="border border-[#e7e9ef] rounded-lg overflow-hidden"><summary className="cursor-pointer list-none px-4 py-3 bg-[#f8f9fb] font-bold text-[#344054] flex justify-between"><span>{i+1}. {c.categoryName}</span><span>{(c.questions||[]).length} questions • {avg(c.averageScore)} / 5</span></summary><div className="overflow-auto"><table className="w-full"><thead><tr><th className={th}>Question</th><th className={th}>Responses</th><th className={th}>Average / 5</th><th className={th}>Total Points</th></tr></thead><tbody>{(c.questions||[]).map((q,j)=><tr key={j}><td className={td} style={{whiteSpace:'normal',minWidth:420}}>{q.questionText}</td><td className={td}>{q.responseCount||0}</td><td className={td}><b>{avg(q.averageScore)}</b></td><td className={td}>{Math.round(Number(q.averageScore||0)*Number(q.responseCount||0)).toLocaleString()}</td></tr>)}</tbody></table></div></details>)}</div></Box>}
function Organization({branches,departments,divisions}){return <><OrgTable title="Branch Comparison" rows={branches} name="branchName"/><OrgTable title="Department Comparison" rows={departments} name="departmentName"/><OrgTable title="Division Comparison" rows={divisions} name="divisionName"/></>}
function OrgTable({title,rows,name}){return <Box title={title} note="Cross-organizational comparison from live reporting data"><div className="overflow-auto"><table className="w-full"><thead><tr><th className={th}>Name</th><th className={th}>Participation</th><th className={th}>Average by Category</th></tr></thead><tbody>{rows.map((r,i)=><tr key={i}><td className={td}><b>{r[name]}</b></td><td className={td}>{pct(r.participationRate)}</td><td className={td}>{Object.entries(r.categoryAverages||{}).map(([k,v])=><span className="inline-block mr-3 text-xs" key={k}>{k}: <b>{avg(v)}</b></span>)}</td></tr>)}</tbody></table></div></Box>}
function Trend({current,previous,range}){if(!range)return <Box title="Trend / Period Comparison" note="Select both From and To dates to compare the selected period with the immediately preceding period"><div className="p-8 text-center text-[#667085]">Choose a date range to activate the live period comparison.</div></Box>; const delta=Number(current?.overallScore||0)-Number(previous?.overallScore||0); const trendData=[{period:'Previous',score:Number(previous?.overallScore||0)},{period:'Current',score:Number(current?.overallScore||0)}]; return <><Box title="Overall Period Trend Chart" note="Live comparison using the selected period and immediately preceding period"><div style={{width:'100%',height:280}}><ResponsiveContainer><LineChart data={trendData} margin={{top:10,right:20,left:0,bottom:10}}><CartesianGrid strokeDasharray="3 3" stroke="#e7e9ef"/><XAxis dataKey="period"/><YAxis domain={[0,5]}/><Tooltip formatter={(v)=>[`${Number(v).toFixed(2)} / 5`,'Average']}/><Line type="monotone" dataKey="score" stroke="#2447d8" strokeWidth={3} dot={{r:5}}/></LineChart></ResponsiveContainer></div></Box><Box title="Overall Period Trend" note={`${range.from} → ${range.to} compared with the immediately preceding period`}><div className="grid md:grid-cols-3 gap-3"><Metric label="Previous Average" value={`${avg(previous?.overallScore)} / 5`} sub="Previous period"/><Metric label="Current Average" value={`${avg(current?.overallScore)} / 5`} sub="Current period"/><Metric label="Change" value={`${delta>=0?'+':''}${avg(delta)}`} sub="Current minus previous"/></div></Box><Box title="Category Trend" note="Category averages compared across the two periods"><div className="overflow-auto"><table className="w-full"><thead><tr><th className={th}>Category</th><th className={th}>Previous</th><th className={th}>Current</th><th className={th}>Change</th></tr></thead><tbody>{(current?.categories||[]).map(c=>{const p=(previous?.categories||[]).find(x=>x.categoryName===c.categoryName);const d=Number(c.averageScore||0)-Number(p?.averageScore||0);return <tr key={c.categoryName}><td className={td}><b>{c.categoryName}</b></td><td className={td}>{avg(p?.averageScore)}</td><td className={td}>{avg(c.averageScore)}</td><td className={`${td} font-bold ${d>=0?'text-[#16794c]':'text-[#b54708]'}`}>{d>=0?'+':''}{avg(d)}</td></tr>})}</tbody></table></div></Box></>}
function Summary({report,categories,measures,responseRate}){const hi=categories[0],lo=categories[categories.length-1];return <><Box title="Management Summary" note="Factual executive snapshot generated from the selected survey data"><div className="grid md:grid-cols-2 gap-4"><div className="border border-[#e7e9ef] rounded-lg p-4"><div className="text-xs text-[#667085]">Overall score</div><div className="text-3xl font-bold text-[#182230] mt-1">{avg(report?.overallScore)} / 5</div><div className="text-xs text-[#667085] mt-2">Response rate: {responseRate.toFixed(1)}%</div></div><div className="border border-[#e7e9ef] rounded-lg p-4"><div className="text-xs text-[#667085]">Rating measures</div><div className="grid grid-cols-2 gap-2 mt-2 text-sm"><span>&gt;4 <b>{measures.gt4.toFixed(1)}%</b></span><span>=4 <b>{measures.eq4.toFixed(1)}%</b></span><span>&lt;4 <b>{measures.lt4.toFixed(1)}%</b></span><span>&lt;3 <b>{measures.lt3.toFixed(1)}%</b></span><span>4 or More <b>{measures.fourPlus.toFixed(1)}%</b></span></div></div></div></Box><Box title="Category Snapshot" note="Highest and lowest category averages in the selected data"><div className="grid md:grid-cols-2 gap-3"><div className="border border-[#e7e9ef] rounded-lg p-4"><div className="text-xs text-[#667085]">Highest category average</div><div className="font-bold text-lg mt-1">{hi?.categoryName||'—'}</div><div className="text-2xl font-bold">{avg(hi?.averageScore)} / 5</div></div><div className="border border-[#e7e9ef] rounded-lg p-4"><div className="text-xs text-[#667085]">Lowest category average</div><div className="font-bold text-lg mt-1">{lo?.categoryName||'—'}</div><div className="text-2xl font-bold">{avg(lo?.averageScore)} / 5</div></div></div></Box></>}
